import { Box, HStack, IconButton, useDisclosure, useToast } from '@chakra-ui/react';
import { useCallback, useRef } from 'react';

import { useDevSandbox } from './DevSandboxProvider';
import DevSandboxTooltip from './DevSandboxTooltip';
import LayoutButton from './LayoutButton';
import SettingsButton from './SettingsButton';
import SwitchConfirmModal from './SwitchConfirmModal';
import {
  CSS_MODE_LABELS,
  FRAMEWORK_LABELS,
  getStarterTemplate,
  getPracticeTemplate,
  isBaseTemplate,
} from './starterTemplates';
import { IEditorSettings, TCSSMode, TEditorFramework } from './types';
import useSandboxTheme from './useSandboxTheme';

const ShareIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

export const ShareButton = () => {
  const toast = useToast();
  const { colors } = useSandboxTheme();

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({
        title: 'Link copied!',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'bottom-right',
      });
    } catch {
      toast({
        title: 'Copy the URL from your browser bar to share.',
        status: 'info',
        duration: 3000,
        position: 'bottom-right',
      });
    }
  };

  return (
    <DevSandboxTooltip label="Copy share link">
      <IconButton
        aria-label="Share"
        icon={<ShareIcon />}
        size="md"
        variant="outline"
        borderColor={colors.controlBorder}
        color={colors.textSecondary}
        bg={colors.controlBg}
        _hover={{
          color: colors.textPrimary,
          bg: colors.hoverBg,
          borderColor: colors.controlBorderHover,
        }}
        _active={{ bg: colors.activeBg }}
        onClick={handleShare}
      />
    </DevSandboxTooltip>
  );
};

interface PendingSwitch {
  settings: Partial<IEditorSettings>;
  targetName: string;
}

const EditorSettingsButton = ({ hideSettings, horizontalOnly: horizontalOnlyProp }: { hideSettings?: boolean; horizontalOnly?: boolean }) => {
  const {
    mode,
    editorSettings,
    codeTabs: { html: rawHtml, css: rawCss, js: rawJs },
    handlers: { onEditorSettingsChange, onEditorSettingsReset, onHtmlChange, onCssChange, onJsChange },
    testCases,
  } = useDevSandbox();

  const isScriptPractice = mode === 'script-practice';
  const { isOpen, onOpen, onClose } = useDisclosure();
  const pendingRef = useRef<PendingSwitch | null>(null);

  const html = rawHtml || '';
  const css = rawCss || '';
  const js = rawJs || '';

  const applySwitch = useCallback((settings: Partial<IEditorSettings>) => {
    const newFramework = (settings.framework || editorSettings.framework) as TEditorFramework;
    const newCssMode = (settings.cssMode || editorSettings.cssMode) as TCSSMode;

    // Apply the settings change first
    onEditorSettingsChange(settings);

    // Apply starter template code
    if (isScriptPractice) {
      const funcName = testCases?.function_name || 'solution';
      const inputNames = testCases?.input_names || [];
      onJsChange(getPracticeTemplate(newFramework, funcName, inputNames));
    } else if (settings.cssMode && !settings.framework) {
      // CSS-only switch: only replace CSS tab
      const template = getStarterTemplate(newFramework, newCssMode);
      onCssChange(template.css);
    } else {
      // Framework switch (or both): replace all tabs
      const template = getStarterTemplate(newFramework, newCssMode);
      onHtmlChange(template.html);
      onCssChange(template.css);
      onJsChange(template.js);
    }
  }, [editorSettings, isScriptPractice, testCases, onEditorSettingsChange, onHtmlChange, onCssChange, onJsChange]);

  const handleSettingsChange = useCallback((partial: Partial<IEditorSettings>) => {
    const isFrameworkChange = 'framework' in partial;
    const isCssModeChange = 'cssMode' in partial;

    // Non-code settings pass through immediately
    if (!isFrameworkChange && !isCssModeChange) {
      onEditorSettingsChange(partial);
      return;
    }

    // Same value — no-op
    if (isFrameworkChange && partial.framework === editorSettings.framework) return;
    if (isCssModeChange && partial.cssMode === editorSettings.cssMode) return;

    // Check if current code is a base template (or empty)
    let isBase = false;

    if (isScriptPractice) {
      const funcName = testCases?.function_name || 'solution';
      const inputNames = testCases?.input_names || [];
      const currentTemplate = getPracticeTemplate(editorSettings.framework, funcName, inputNames);
      isBase = isBaseTemplate(js, currentTemplate) || js.trim() === '';
    } else if (isCssModeChange && !isFrameworkChange) {
      // CSS-only switch: check only CSS tab
      const currentTemplate = getStarterTemplate(editorSettings.framework, editorSettings.cssMode);
      isBase = isBaseTemplate(css, currentTemplate.css) || css.trim() === '';
    } else {
      // Framework switch: check all tabs
      const currentTemplate = getStarterTemplate(editorSettings.framework, editorSettings.cssMode);
      isBase =
        (isBaseTemplate(html, currentTemplate.html) || html.trim() === '') &&
        (isBaseTemplate(css, currentTemplate.css) || css.trim() === '') &&
        (isBaseTemplate(js, currentTemplate.js) || js.trim() === '');
    }

    if (isBase) {
      // Silent switch — code matches starter or is empty
      applySwitch(partial);
    } else {
      // Custom code — show confirmation
      const targetName = isFrameworkChange
        ? FRAMEWORK_LABELS[partial.framework as TEditorFramework]
        : CSS_MODE_LABELS[partial.cssMode as TCSSMode];
      pendingRef.current = { settings: partial, targetName };
      onOpen();
    }
  }, [editorSettings, isScriptPractice, testCases, html, css, js, applySwitch, onEditorSettingsChange, onOpen]);

  const handleConfirm = useCallback(() => {
    if (pendingRef.current) {
      applySwitch(pendingRef.current.settings);
      pendingRef.current = null;
    }
  }, [applySwitch]);

  const handleModalClose = useCallback(() => {
    pendingRef.current = null;
    onClose();
  }, [onClose]);

  return (
    <>
      <HStack spacing={2}>
        {!hideSettings && (
          <Box data-tour="editor-settings" display="inline-flex">
            <SettingsButton
              settings={editorSettings}
              onSettingsChange={handleSettingsChange}
              onReset={onEditorSettingsReset}
              variant="topbar"
              mode={mode}
            />
          </Box>
        )}
        <Box data-tour="layout-mode" display="inline-flex">
          <LayoutButton
            layout={editorSettings.layout}
            onLayoutChange={(layout) => onEditorSettingsChange({ layout })}
            horizontalOnly={horizontalOnlyProp || isScriptPractice}
          />
        </Box>
      </HStack>
      <SwitchConfirmModal
        isOpen={isOpen}
        onClose={handleModalClose}
        onConfirm={handleConfirm}
        targetName={pendingRef.current?.targetName || ''}
      />
    </>
  );
};

export default EditorSettingsButton;
